import React, { useState, useEffect, useMemo } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { Block, isPrestation, PrixReliability } from '../types';
import { BlockCalculationService } from '../services';

type Reliability = PrixReliability;

interface ImportAnimationOverlayProps {
  targetBlocks: Block[];
  reliabilityMap?: Record<string, Reliability>;
  onComplete: () => void;
}

const RELIABILITY_CONFIG: Record<Reliability, { label: string; dot: string; text: string; bg: string }> = {
  high:   { label: 'Fiable',     dot: 'bg-green-500',  text: 'text-green-700',  bg: 'bg-green-50 border-green-200' },
  medium: { label: 'Incertain',  dot: 'bg-orange-400', text: 'text-orange-700', bg: 'bg-orange-50 border-orange-200' },
  low:    { label: 'Douteux',    dot: 'bg-red-500',    text: 'text-red-700',    bg: 'bg-red-50 border-red-200' },
};


const DELAY_BETWEEN = 430; // ms entre chaque prestation
const ANIM_DURATION = 520; // ms pour compter le prix
const STEPS = 20;

const ImportAnimationOverlay: React.FC<ImportAnimationOverlayProps> = ({
  targetBlocks: rawTargetBlocks,
  reliabilityMap: reliabilityMapProp,
  onComplete
}) => {
  const targetBlocks = useMemo(
    () => rawTargetBlocks.filter(b => !b.content.isVentilationCostLine),
    [rawTargetBlocks]
  );
  const [displayBlocks, setDisplayBlocks] = useState<Block[]>(() =>
    targetBlocks.map(b =>
      isPrestation(b)
        ? { ...b, content: { ...b.content, prixUnitaire: '', prixTotal: '' } }
        : b
    )
  );
  const [animatingId, setAnimatingId] = useState<string | null>(null);
  const [doneIds, setDoneIds] = useState<Set<string>>(new Set());

  // Utiliser la reliability map fournie par le parent (pour cohérence avec l'éditeur),
  // ou en générer une aléatoirement en fallback.
  const reliabilityMap = useMemo<Record<string, Reliability>>(() => {
    if (reliabilityMapProp && Object.keys(reliabilityMapProp).length > 0) {
      return reliabilityMapProp;
    }
    const map: Record<string, Reliability> = {};
    targetBlocks.filter(b => isPrestation(b)).forEach(b => {
      const r = Math.random();
      map[b.id] = r < 0.45 ? 'high' : r < 0.75 ? 'medium' : 'low';
    });
    return map;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Réinitialiser l'état au démarrage (nécessaire pour le double-mount en StrictMode)
    setDisplayBlocks(
      targetBlocks.map(b =>
        isPrestation(b)
          ? { ...b, content: { ...b.content, prixUnitaire: '', prixTotal: '' } }
          : b
      )
    );
    setAnimatingId(null);
    setDoneIds(new Set());

    const prestations = targetBlocks.filter(b => isPrestation(b));

    // Variables locales pour le cleanup (pas de refs — évite le bug StrictMode)
    const timeoutIds: ReturnType<typeof setTimeout>[] = [];
    const intervalIds: ReturnType<typeof setInterval>[] = [];

    prestations.forEach((p, i) => {
      const t = setTimeout(() => {
        setAnimatingId(p.id);

        const targetPU = parseFloat(p.content.prixUnitaire || '0');
        const targetPT = parseFloat(p.content.prixTotal || '0');
        let step = 0;

        const iv = setInterval(() => {
          step++;
          const progress = 1 - Math.pow(1 - step / STEPS, 2); // ease-out

          setDisplayBlocks(prev =>
            prev.map(b =>
              b.id === p.id
                ? {
                    ...b,
                    content: {
                      ...b.content,
                      prixUnitaire: Math.round(targetPU * progress).toString(),
                      prixTotal: Math.round(targetPT * progress).toString()
                    }
                  }
                : b
            )
          );

          if (step >= STEPS) {
            clearInterval(iv);
            setAnimatingId(null);
            setDoneIds(prev => { const next = new Set(Array.from(prev)); next.add(p.id); return next; });

            // Fixer les valeurs exactes finales
            setDisplayBlocks(prev =>
              prev.map(b =>
                b.id === p.id
                  ? {
                      ...b,
                      content: {
                        ...b.content,
                        prixUnitaire: p.content.prixUnitaire || '',
                        prixTotal: p.content.prixTotal || ''
                      }
                    }
                  : b
              )
            );

            if (i === prestations.length - 1) {
              const done = setTimeout(onComplete, 500);
              timeoutIds.push(done);
            }
          }
        }, ANIM_DURATION / STEPS);

        intervalIds.push(iv);
      }, i * DELAY_BETWEEN);

      timeoutIds.push(t);
    });

    return () => {
      timeoutIds.forEach(clearTimeout);
      intervalIds.forEach(clearInterval);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const renderBlock = (block: Block) => {
    const isPrest = isPrestation(block);
    const isAnim = animatingId === block.id;
    const isDone = doneIds.has(block.id);
    const indentPl = block.level * 16;

    if (!isPrest) {
      const total = BlockCalculationService.calculatePrestationTotal(block, displayBlocks);
      return (
        <div key={block.id} className="flex items-center py-2 border-b border-gray-100 bg-gray-50">
          <div className="w-8 flex-shrink-0" />
          <div className="w-5 flex-shrink-0" />
          <div className="flex-1 min-w-0 flex items-center gap-1.5" style={{ paddingLeft: `${indentPl + 4}px` }}>
            <ChevronDownIcon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
            <span className="font-semibold text-gray-800 text-sm truncate">{block.content.titre}</span>
          </div>
          <div className="w-14 flex-shrink-0" />
          <div className="w-16 flex-shrink-0" />
          <div className="w-28 flex-shrink-0" />
          <div className="w-28 flex-shrink-0 text-right pr-2">
            <span className="font-semibold text-sm text-violet-600">
              {total > 0 ? `${BlockCalculationService.formatPrice(total)} €` : '—'}
            </span>
          </div>
          <div className="w-8 flex-shrink-0" />
          <div className="w-8 flex-shrink-0" />
          <div className="w-14 flex-shrink-0" />
        </div>
      );
    }

    const pu = block.content.prixUnitaire || '';
    const pt = block.content.prixTotal || '';
    const hasValue = pu !== '' || isAnim;
    const reliability = reliabilityMap[block.id];

    return (
      <div
        key={block.id}
        className={`flex items-center py-2.5 border-b border-gray-100 transition-colors duration-150 ${
          isAnim ? 'bg-violet-50' : !isDone ? 'opacity-40' : ''
        }`}
      >
        <div className="w-8 flex-shrink-0" />
        <div className="w-5 flex-shrink-0" />
        <div className="flex-1 min-w-0" style={{ paddingLeft: `${indentPl + 4}px` }}>
          <div className={`text-sm truncate ${isDone || isAnim ? 'text-gray-800' : 'text-gray-400'}`}>
            {block.content.intitule}
          </div>
          {block.content.categorie && block.content.categorie !== 'Autre' && (
            <div className="text-xs text-gray-400 truncate">{block.content.categorie}</div>
          )}
        </div>
        <div className="w-14 flex-shrink-0 text-center text-sm text-gray-500">
          {block.content.unite || '—'}
        </div>
        <div className="w-16 flex-shrink-0 text-right text-sm text-gray-700 pr-1">
          {block.content.quantite || '—'}
        </div>
        <div className="w-28 flex-shrink-0 text-right pr-2">
          <div className="flex items-center justify-end gap-1">
            {reliability && isDone && (
              <span className={`w-2 h-2 rounded-full flex-shrink-0 ${RELIABILITY_CONFIG[reliability].dot}`} />
            )}
            <span className={`text-sm font-mono ${isAnim ? 'text-violet-700 font-semibold' : 'text-violet-600'}`}>
              {hasValue ? pu : '—'}
              {isAnim && (
                <span className="inline-block w-0.5 h-3.5 bg-violet-500 ml-0.5 align-middle animate-pulse" />
              )}
            </span>
          </div>
        </div>
        <div className="w-28 flex-shrink-0 text-right pr-2">
          <span className={`text-sm font-semibold font-mono ${isAnim ? 'text-violet-700' : 'text-violet-600'}`}>
            {pt
              ? `${BlockCalculationService.formatPrice(parseFloat(pt))} €`
              : isAnim ? '…' : '—'}
          </span>
        </div>
        <div className="w-8 flex-shrink-0" />
        <div className="w-8 flex-shrink-0" />
        <div className="w-14 flex-shrink-0" />
      </div>
    );
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 -mt-2 overflow-hidden">
      {/* En-tête colonnes — identique à BlockList */}
      <div className="flex items-center py-1.5 bg-gray-50 border-b-2 border-gray-200 text-xs font-medium text-gray-400 uppercase tracking-wider">
        <div className="w-8 flex-shrink-0" />
        <div className="w-5 flex-shrink-0" />
        <div className="flex-1 min-w-0 pl-1">Désignation</div>
        <div className="w-14 flex-shrink-0 text-center">Unité</div>
        <div className="w-16 flex-shrink-0 text-right">Qté</div>
        <div className="w-28 flex-shrink-0 text-right pr-2">P.U.</div>
        <div className="w-28 flex-shrink-0 text-right pr-2">Total HT</div>
        <div className="w-8 flex-shrink-0" />
        <div className="w-8 flex-shrink-0" />
        <div className="w-14 flex-shrink-0" />
      </div>
      <div className="min-h-[400px]">
        {displayBlocks.map(block => renderBlock(block))}
      </div>
    </div>
  );
};

export default ImportAnimationOverlay;
